/**
 * Tenant: Bulk Pickup Request — only visible if their property has bulk_collection.enabled.
 * Auto-fills apartment/property from sign-up. Photo upload + description.
 */
import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Boxes, Camera, Calendar, Info } from 'lucide-react';
import { toast } from 'sonner';
import PhotoCapture from './PhotoCapture';

const API = process.env.REACT_APP_BACKEND_URL;

const FREQ_LABELS = {
  weekly: 'Weekly',
  biweekly: 'Every 2 weeks',
  monthly: 'Monthly',
  on_request: 'On request',
};

export default function TenantBulkPickup() {
  const [policy, setPolicy] = useState({ enabled: false });
  const [propertyName, setPropertyName] = useState('');
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);

  const [form, setForm] = useState({
    items_description: '',
    item_count: 1,
    requested_for_date: '',
    notes: '',
    photos: [],
  });
  const [loading, setLoading] = useState(false);

  const loadPolicy = async () => {
    try {
      const res = await fetch(`${API}/api/ops/bulk-policy/my`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPolicy(data.bulk_collection || { enabled: false });
        setPropertyName(data.property_name || '');
      }
    } catch (_) { /* noop */ }
  };

  const loadRequests = async () => {
    try {
      const res = await fetch(`${API}/api/ops/bulk-pickup/my`, { credentials: 'include' });
      if (res.ok) setRequests(await res.json());
    } catch (_) { /* noop */ }
  };

  useEffect(() => { loadPolicy(); loadRequests(); }, []);

  if (!policy.enabled) return null; // Gated — feature only appears for opted-in properties

  const submit = async () => {
    if (!form.items_description.trim()) {
      return toast.error('Please describe the items');
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ops/bulk-pickup`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Could not submit');
      } else {
        toast.success('Bulk pickup requested. Property management will confirm a date.');
        setOpen(false);
        setForm({ items_description: '', item_count: 1, requested_for_date: '', notes: '', photos: [] });
        loadRequests();
      }
    } finally { setLoading(false); }
  };

  const statusColor = (s) => ({
    submitted: 'bg-zinc-600',
    scheduled: 'bg-amber-600 text-black',
    completed: 'bg-emerald-600',
    cancelled: 'bg-red-700',
  }[s] || 'bg-zinc-600');

  return (
    <Card className="p-6 mb-8 border-2 border-[#FFD60A] bg-white" data-testid="tenant-bulk-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2 text-[#3D3D3D]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Boxes className="h-6 w-6 text-[#C9A962]" /> BULK PICKUP
          </h2>
          <p className="text-sm text-zinc-600 mt-1">
            Got an old couch, mattress, or other large items? Request a bulk pickup right from your app.
            {propertyName && <span className="block text-xs text-zinc-500 mt-1">Service available at {propertyName}.</span>}
          </p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          className="bg-[#C9A962] hover:bg-[#b6964e] text-black font-bold"
          data-testid="tenant-bulk-request-btn"
        >
          <Camera className="h-4 w-4 mr-2" /> Request Bulk Pickup
        </Button>
      </div>

      {/* Policy details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 text-sm">
        <PolicyLine label="Schedule" value={
          policy.frequency
            ? `${FREQ_LABELS[policy.frequency] || policy.frequency}${policy.time_window ? ` · ${policy.time_window}` : ''}`
            : 'Confirmed by property management'
        } />
        <PolicyLine label="Notice required" value={`${policy.notice_required_hours || 48} hours`} />
        {policy.accepted_items && <PolicyLine label="Accepted items" value={policy.accepted_items} />}
        {policy.not_accepted_items && <PolicyLine label="NOT accepted" value={policy.not_accepted_items} />}
        {policy.max_items > 0 && <PolicyLine label="Max items / pickup" value={policy.max_items} />}
        {policy.additional_fee > 0 && <PolicyLine label="Additional fee" value={`$${Number(policy.additional_fee).toFixed(2)}`} />}
        {policy.instructions && <PolicyLine label="Instructions" value={policy.instructions} full />}
      </div>

      {/* History */}
      {requests.length > 0 && (
        <div className="mt-5 border-t border-zinc-200 pt-3">
          <h4 className="text-sm font-bold text-zinc-700 mb-2 flex items-center gap-2">
            <Calendar className="h-4 w-4" /> Your bulk-pickup history
          </h4>
          <ul className="space-y-1 max-h-44 overflow-y-auto pr-2">
            {requests.slice(0, 8).map((r) => (
              <li key={r.request_id} className="flex items-center justify-between text-xs text-zinc-700" data-testid={`tenant-bulk-row-${r.request_id}`}>
                <span>
                  {new Date(r.created_at).toLocaleDateString()} · {r.item_count} item(s) · {r.items_description.slice(0, 40)}
                </span>
                <Badge className={statusColor(r.status)}>{r.status}{r.scheduled_for ? ` (${r.scheduled_for})` : ''}</Badge>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Request dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#3D3D3D]">
              <Boxes className="h-5 w-5 text-[#C9A962]" /> Request Bulk Pickup
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="bulk-desc">Describe the items *</Label>
              <Textarea
                id="bulk-desc"
                placeholder="e.g., 1 old sofa, 1 mattress, 2 broken chairs"
                value={form.items_description}
                onChange={(e) => setForm({ ...form, items_description: e.target.value })}
                rows={3}
                data-testid="tenant-bulk-desc-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="bulk-count">Item count</Label>
                <Input
                  id="bulk-count"
                  type="number"
                  min={1}
                  value={form.item_count}
                  onChange={(e) => setForm({ ...form, item_count: parseInt(e.target.value) || 1 })}
                  data-testid="tenant-bulk-count-input"
                />
              </div>
              <div>
                <Label htmlFor="bulk-date">Preferred date (optional)</Label>
                <Input
                  id="bulk-date"
                  type="date"
                  value={form.requested_for_date}
                  onChange={(e) => setForm({ ...form, requested_for_date: e.target.value })}
                  data-testid="tenant-bulk-date-input"
                />
              </div>
            </div>
            <div>
              <Label>Photos (recommended) — up to 6</Label>
              <PhotoCapture
                photos={form.photos}
                onPhotosChange={(photos) => setForm({ ...form, photos })}
                dataTestId="tenant-bulk-photos"
              />
            </div>
            <div>
              <Label htmlFor="bulk-notes">Notes (optional)</Label>
              <Textarea
                id="bulk-notes"
                placeholder="Where the items will be — e.g., 'side gate by Building A'"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                data-testid="tenant-bulk-notes-input"
              />
            </div>
            {policy.notice_required_hours > 0 && (
              <div className="flex items-start gap-2 text-xs text-zinc-600 bg-amber-50 border border-amber-200 rounded p-2">
                <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <span>This property requires {policy.notice_required_hours}h advance notice. Property management will confirm the actual pickup date.</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={loading}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={loading}
              className="bg-[#C9A962] hover:bg-[#b6964e] text-black font-bold"
              data-testid="tenant-bulk-submit-btn"
            >
              {loading ? 'Submitting…' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PolicyLine({ label, value, full }) {
  return (
    <div className={`text-xs text-zinc-700 ${full ? 'md:col-span-2' : ''}`}>
      <span className="font-semibold text-zinc-500 mr-1">{label}:</span>{value}
    </div>
  );
}
